import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const PRESETS = [
  { label: '5 min', seconds: 5 * 60 },
  { label: '10 min', seconds: 10 * 60 },
  { label: '15 min', seconds: 15 * 60 },
  { label: '1 hour', seconds: 60 * 60 },
];

const DAY_DATA: Record<number, { label: string; subtitle: string }> = {
  1: { label: 'Day 1 of 3', subtitle: 'Day 1 — The Old Model Is Breaking.' },
  2: { label: 'Day 2 of 3', subtitle: 'Day 2 — Promote & Profit.' },
  3: { label: 'Day 3 of 3', subtitle: 'Day 3 — Progress, Pathways and Presence.' },
};

const CREDENTIALS = [
  'International Trainer of NLP',
  'International Trainer of Time Line Therapy\u00ae',
  'International Trainer of Hypnotherapy',
  'International Trainer of NLP Coaching',
  'Best-Selling Author of \u2018Limitless\u2019',
  'Author of \u2018The New Model of Coaching\u2019',
  'Author of \u2018The New Model of NLP\u2019',
];

function formatTime(s: number) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export function CountdownTimer() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isInvitationSpecial = location.pathname === '/invitationspecial';
  const dayParam = parseInt(searchParams.get('day') ?? '1', 10);
  const day = DAY_DATA[dayParam] ?? DAY_DATA[1];

  const [presetIdx, setPresetIdx] = useState(2); // default 15 min
  const [total, setTotal] = useState(PRESETS[2].seconds);
  const [remaining, setRemaining] = useState(PRESETS[2].seconds);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (running && remaining > 0) {
      intervalRef.current = setInterval(() => {
        setRemaining(prev => {
          if (prev <= 1) {
            clearInterval(intervalRef.current!);
            setRunning(false);
            setFinished(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [running]);

  const handlePreset = (idx: number) => {
    if (running) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    setPresetIdx(idx);
    setTotal(PRESETS[idx].seconds);
    setRemaining(PRESETS[idx].seconds);
    setFinished(false);
  };

  const handleReset = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setRunning(false);
    setFinished(false);
    setRemaining(total);
  };

  const progress = remaining / total;
  const radius = 110;
  const circumference = 2 * Math.PI * radius;
  const isLow = remaining <= 60 && remaining > 0;

  return (
    <div className="min-h-screen bg-cream-100 flex flex-col" style={{ fontFamily: 'Urbanist, sans-serif' }}>

      {/* ── Invitation Special Banner ─────────────────────────── */}
      {isInvitationSpecial && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full bg-chocolate-900 text-cream-100 py-3 px-6 text-center"
        >
          <p className="text-sm font-semibold tracking-wide">
            <span className="text-sage-400 font-bold">2</span> of{' '}
            <span className="relative inline-block">
              <span className="text-cream-100">3</span>
              <span className="absolute left-0 right-0 top-1/2 h-0.5 bg-cream-100 -rotate-12" />
            </span>{' '}
            founding spots left
          </p>
        </motion.div>
      )}

      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="flex items-center justify-between px-10 pt-8 pb-5">
        <img
          src="/human_potential_os_logo_V2_cropped.png"
          alt="NLP OS"
          className="h-11 object-contain object-left"
        />
        <span
          className="text-xs tracking-[0.2em] uppercase text-chocolate-500 font-medium"
        >
          — {day.label} —
        </span>
      </header>

      {/* thin rule */}
      <div className="mx-10 h-px bg-chocolate-200 opacity-50" />

      {/* ── Body ─────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col lg:flex-row">

        {/* ── LEFT: welcome + timer ─────────────────────────────── */}
        <div className="flex-1 flex flex-col justify-center px-10 py-10 lg:py-14 lg:pr-16">

          {/* eyebrow */}
          <p className="text-xs tracking-[0.25em] uppercase text-chocolate-400 font-medium mb-5">
            — Foundations Certification · 3 Days —
          </p>

          {/* headline */}
          <h1
            className="text-chocolate-900 leading-tight mb-3"
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 'clamp(28px, 3.5vw, 52px)',
              fontWeight: 500,
              letterSpacing: '-0.02em',
            }}
          >
            Welcome to the<br />
            <em className="text-sage-600">NLP OS</em><br />
            Foundations Certification.
          </h1>

          {/* day subtitle */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-0.5 bg-sage-500" />
            <p
              className="text-chocolate-500 italic"
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 'clamp(14px, 1.6vw, 20px)',
                fontWeight: 300,
              }}
            >
              {day.subtitle}
            </p>
          </div>

          {/* ── Preset selector ─────────────────────── */}
          <div className="flex items-center gap-2 mb-8">
            <span className="text-xs tracking-widest uppercase text-chocolate-400 mr-2">Duration</span>
            {PRESETS.map((p, i) => (
              <button
                key={p.label}
                disabled={running}
                onClick={() => handlePreset(i)}
                className="px-4 py-1.5 rounded-full text-sm font-semibold transition-all"
                style={{
                  background: i === presetIdx ? '#558347' : 'transparent',
                  color: i === presetIdx ? '#fff' : '#8B6347',
                  border: i === presetIdx ? 'none' : '1px solid #C5A795',
                  opacity: running ? 0.5 : 1,
                  cursor: running ? 'not-allowed' : 'pointer',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* ── Timer ring ──────────────────────────── */}
          <div className="flex items-center gap-10">
            <div className="relative flex items-center justify-center flex-shrink-0">
              <svg width="260" height="260" className="-rotate-90">
                <circle
                  cx="130" cy="130" r={radius}
                  fill="none"
                  stroke="#DBC9BE"
                  strokeWidth="5"
                />
                <motion.circle
                  cx="130" cy="130" r={radius}
                  fill="none"
                  stroke={isLow ? '#c0392b' : '#558347'}
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  animate={{
                    strokeDashoffset: circumference * (1 - progress),
                    stroke: isLow ? '#c0392b' : '#558347',
                  }}
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                />
              </svg>

              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <AnimatePresence mode="wait">
                  {finished ? (
                    <motion.div
                      key="done"
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-center px-2"
                    >
                      <p
                        className="text-chocolate-700 italic"
                        style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 500 }}
                      >
                        Time&apos;s Up
                      </p>
                    </motion.div>
                  ) : (
                    <motion.div key="time" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
                      <span
                        className="block font-mono leading-none"
                        style={{
                          fontSize: remaining >= 3600 ? 38 : 52,
                          fontWeight: 300,
                          letterSpacing: '-0.04em',
                          color: isLow ? '#c0392b' : '#3d2b20',
                        }}
                      >
                        {formatTime(remaining)}
                      </span>
                      <span className="block text-xs tracking-widest uppercase text-chocolate-400 mt-1.5">
                        {running ? 'counting down' : remaining === total ? 'ready' : 'paused'}
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* controls */}
            <div className="flex flex-col gap-3">
              {!finished && (
                <button
                  onClick={() => setRunning(r => !r)}
                  className="px-7 py-3 rounded-full font-semibold text-sm tracking-wide transition-all"
                  style={{
                    background: running ? '#f5f0ea' : '#558347',
                    color: running ? '#5c3d2e' : '#fff',
                    border: running ? '1px solid #C5A795' : 'none',
                    minWidth: 110,
                  }}
                >
                  {running ? 'Pause' : remaining === total ? 'Start' : 'Resume'}
                </button>
              )}
              <button
                onClick={handleReset}
                className="px-7 py-3 rounded-full font-semibold text-sm tracking-wide border transition-all"
                style={{
                  background: 'transparent',
                  color: '#8B6347',
                  borderColor: '#C5A795',
                  minWidth: 110,
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* ── vertical divider (lg+) ───────────────────────────── */}
        <div className="hidden lg:block w-px bg-chocolate-200 opacity-40 my-12" />

        {/* ── RIGHT: photo + credentials ─────────────────────────── */}
        <div className="lg:w-[420px] xl:w-[480px] flex flex-col items-center justify-center px-10 py-10 lg:py-14 lg:pl-12">

          {/* portrait photo */}
          <div
            className="w-52 h-60 xl:w-60 xl:h-72 rounded-2xl overflow-hidden mb-6 flex-shrink-0"
            style={{ boxShadow: '0 12px 40px rgba(61,43,32,0.18)' }}
          >
            <img
              src="/Patrick_Profile_Under_2MB_q95.jpg"
              alt="Patrick Dahdal"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* name */}
          <h2
            className="text-chocolate-900 text-center mb-0.5"
            style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}
          >
            Patrick Dahdal
          </h2>

          {/* title */}
          <p className="text-sage-600 text-center text-sm font-semibold mb-0.5">
            Founder, NLP OS™
          </p>
          <p className="text-chocolate-400 text-center text-xs tracking-wide italic mb-5">
            The Operating System for Human Potential
          </p>

          {/* rule */}
          <div className="w-10 h-0.5 bg-sage-400 mb-5" />

          {/* credentials */}
          <ul className="space-y-2 w-full">
            {CREDENTIALS.map(c => (
              <li key={c} className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-chocolate-300 flex-shrink-0" />
                <span className="text-chocolate-600 text-sm leading-snug">{c}</span>
              </li>
            ))}
          </ul>

          {/* url */}
          <p className="mt-6 text-xs tracking-widest uppercase text-chocolate-300">
            humanpotentialos.com
          </p>
        </div>
      </div>
    </div>
  );
}
